import type { DroneView, MapPositions, MissionDashboard } from '@sentinel/shared';

export interface MarkerChange {
  /** New position; null or absent: the marker stays where it was (a drone without GPS fix). */
  position?: { lng: number; lat: number } | null | undefined;
  /** New view cone of a drone; null removes it, absent keeps the one it had. */
  view?: DroneView | null | undefined;
}

/** The dashboard with one marker changed (a drone moved, turned its camera): the rest of the
 *  aggregate stays as it was, so a drone flying does not reload every panel of the screen.
 *  Unknown marker, or nothing to change: the same dashboard. */
export function moveMarker(
  dashboard: MissionDashboard | undefined,
  markerId: string,
  change: MarkerChange,
): MissionDashboard | undefined {
  const { position, view } = change;
  const nothing = (position === undefined || position === null) && view === undefined;
  if (dashboard === undefined || nothing || !dashboard.markers.some((m) => m.id === markerId))
    return dashboard;
  const patch = {
    ...(position === undefined || position === null ? {} : position),
    ...(view === undefined ? {} : { view }),
  };
  return {
    ...dashboard,
    markers: dashboard.markers.map((m) => (m.id === markerId ? { ...m, ...patch } : m)),
  };
}

/** The positions of the map page with one marker moved, received now; the same object when the
 *  marker is not in view (the next reload of the area brings it). */
export function movePosition(
  positions: MapPositions | undefined,
  markerId: string,
  change: MarkerChange,
  at: string,
): MapPositions | undefined {
  const { position, view } = change;
  const nothing = (position === undefined || position === null) && view === undefined;
  if (positions === undefined || nothing || !positions.items.some((m) => m.id === markerId))
    return positions;
  // A tracker moved it: the position is no longer the one placed by hand, if it was.
  const moved =
    position === undefined || position === null
      ? {}
      : { ...position, positionAt: at, positionSource: null };
  const patch = { ...moved, ...(view === undefined ? {} : { view }) };
  return {
    ...positions,
    items: positions.items.map((m) => (m.id === markerId ? { ...m, ...patch } : m)),
  };
}
